import I18n from "components/materials/I18n";
import OfficesList from "./OfficesList";
import { useManualSubscriptionContext } from "./contexts/ManualSubscriptionContext";
import { useState } from "react";
import UsersList from "./UsersList";
import SelectClient from "./SelectClient";
import CreateClient from "components/pages/Subscriptions/physicalPerson/CreateClient";
import CreateCorporation from "components/pages/Subscriptions/corporation/CreateCorporation";
import CreateMinimalClientPP from "./CreateMinimalClientPP";
import CreateMinimalClientPM from "./CreateMinimalClientPM";
import SelectProducts from "./SelectProducts";
import useHead from "hooks/useHead";
import classes from "./classes.module.scss";
import Typography, { ITypo } from "components/elements/Typography";
import Button, { EButtonVariant } from "components/elements/Button";
import { useNavigate } from "react-router-dom";
import ModuleConfig from "configs/ModuleConfig";
import { container } from "tsyringe";
import AskCoSubscriberModal from "./AskCoSubscriberModal";
import useOpenable from "hooks/useOpenable";
import ClientAdminApi from "api/wenImmo/admin/ClientApi";
import CreateCoSubscriber from "./CreateCoSubscriber";

enum EManualSubscriptionSteps {
	SELECT_OFFICE = "select_office",
	SELECT_CGP = "select_cgp",
	SELECT_CLIENT = "select_client",
	CREATE_CLIENT_PP = "create_client_pp",
	CREATE_CLIENT_PM = "create_client_pm",
	CREATE_MINIMAL_CLIENT_PP = "create_minimal_client_pp",
	CREATE_MINIMAL_CLIENT_PM = "create_minimal_client_pm",
	ASK_CO_SUBSCRIBER = "ask_co_subscriber",
	CREATE_CO_SUBSCRIBER_PP = "create_co_subscriber_pp",
	SELECT_PRODUCTS = "select_products",
}

const modules = container.resolve(ModuleConfig).get().modules;
const clientAdminApi = container.resolve(ClientAdminApi);

export default function ManualSubscription() {
	const { setOfficeId, officeId, setCgpId, cgpId, setClientSheetId, clientSheetId, setIsCoSubscription, isCoSubscription } = useManualSubscriptionContext();
	const [step, setStep] = useState<EManualSubscriptionSteps>(EManualSubscriptionSteps.SELECT_OFFICE);
	const [subscriberType, setSubscriberType] = useState<"physical" | "legal">("physical");
	const navigate = useNavigate();
	const askCoSubscriberModal = useOpenable();

	useHead({
		title: I18n.asset.pages.mo_manual_subscription.page_title,
	});

	const onSelectOffice = (officeId: string) => {
		setOfficeId(officeId);
		setStep(EManualSubscriptionSteps.SELECT_CGP);
	};

	const onSelectCgp = (cgpId: string) => {
		setCgpId(cgpId);
		setStep(EManualSubscriptionSteps.SELECT_CLIENT);
	};

	const onSelectClient = (clientSheetId: string, type: "physical" | "legal") => {
		setClientSheetId(clientSheetId);
		setSubscriberType(type);

		if (type === "physical") {
			// Pour les personnes physiques, demander si c'est une co-souscription
			askCoSubscriberModal.open();
		} else {
			// Pour les personnes morales, pas de co-souscription
			setIsCoSubscription(false);
			setStep(EManualSubscriptionSteps.SELECT_PRODUCTS);
		}
	};

	const onCreateNewClient = (type: "PP" | "PM", minimal: boolean = false) => {
		setSubscriberType(type === "PP" ? "physical" : "legal");
		if (minimal) {
			setStep(type === "PP" ? EManualSubscriptionSteps.CREATE_MINIMAL_CLIENT_PP : EManualSubscriptionSteps.CREATE_MINIMAL_CLIENT_PM);
		} else {
			setStep(type === "PP" ? EManualSubscriptionSteps.CREATE_CLIENT_PP : EManualSubscriptionSteps.CREATE_CLIENT_PM);
		}
	};

	const onClientCreated = (clientSheetId: string) => {
		setClientSheetId(clientSheetId);

		if (subscriberType === "physical") {
			// Pour les personnes physiques, demander si c'est une co-souscription
			askCoSubscriberModal.open();
		} else {
			// Pour les personnes morales, pas de co-souscription
			setIsCoSubscription(false);
			setStep(EManualSubscriptionSteps.SELECT_PRODUCTS);
		}
	};

	const onCancelClientCreation = () => {
		setStep(EManualSubscriptionSteps.SELECT_CLIENT);
	};

	const onCoSubscriberNo = () => {
		setIsCoSubscription(false);
		askCoSubscriberModal.close();
		setStep(EManualSubscriptionSteps.SELECT_PRODUCTS);
	};

	const onCoSubscriberYes = async () => {
		askCoSubscriberModal.close();
		setIsCoSubscription(true);

		// Vérifier si le client a déjà un spouse
		const clientSheet = await clientAdminApi.getClientSheetByIdAdmin(clientSheetId);

		if (clientSheet.spouseId) {
			// Le client a déjà un spouse, aller directement à la sélection des produits
			setStep(EManualSubscriptionSteps.SELECT_PRODUCTS);
		} else {
			// Le client n'a pas de spouse, aller à la création du co-souscripteur
			setStep(EManualSubscriptionSteps.CREATE_CO_SUBSCRIBER_PP);
		}
	};

	const onCoSubscriberCreated = (clientSheetId: string) => {
		// Le clientSheetId retourné est celui du principal (nouveau, avec spouse lié)
		setClientSheetId(clientSheetId);
		setStep(EManualSubscriptionSteps.SELECT_PRODUCTS);
	};

	const onCancelCoSubscriberCreation = () => {
		setIsCoSubscription(false);
		setStep(EManualSubscriptionSteps.SELECT_CLIENT);
	};

	return (
		<main className={classes["root"]}>
			<Button variant={EButtonVariant.TEXT} onClick={() => navigate(modules.pages.Home.props.path)}>
				{I18n.asset.common.back_to_home}
			</Button>
			<Typography typo={ITypo.H1}>{I18n.asset.pages.mo_manual_subscription.steps[step]}</Typography>
			{step === EManualSubscriptionSteps.SELECT_OFFICE && <OfficesList onSelectOffice={onSelectOffice} />}
			{step === EManualSubscriptionSteps.SELECT_CGP && <UsersList officeId={officeId} onSelectCgp={onSelectCgp} />}
			{step === EManualSubscriptionSteps.SELECT_CLIENT && <SelectClient officeId={officeId} onSelectClient={onSelectClient} onCreateNewClient={onCreateNewClient} />}
			{step === EManualSubscriptionSteps.CREATE_CLIENT_PP && (
				<CreateClient
					createOnBehalfOf={{
						creatorId: cgpId,
						officeId,
						onComplete: onClientCreated,
						onCancel: onCancelClientCreation,
					}}
				/>
			)}
			{step === EManualSubscriptionSteps.CREATE_CLIENT_PM && (
				<CreateCorporation
					createOnBehalfOf={{
						creatorId: cgpId,
						officeId,
						onComplete: onClientCreated,
						onCancel: onCancelClientCreation,
					}}
				/>
			)}
			{step === EManualSubscriptionSteps.CREATE_MINIMAL_CLIENT_PP && (
				<CreateMinimalClientPP creatorId={cgpId} onClientCreated={onClientCreated} onCancel={onCancelClientCreation} />
			)}
			{step === EManualSubscriptionSteps.CREATE_MINIMAL_CLIENT_PM && (
				<CreateMinimalClientPM creatorId={cgpId} onCorporationCreated={onClientCreated} onCancel={onCancelClientCreation} />
			)}
			{step === EManualSubscriptionSteps.CREATE_CO_SUBSCRIBER_PP && (
				<CreateCoSubscriber clientSheetId={clientSheetId} creatorId={cgpId} onComplete={onCoSubscriberCreated} onCancel={onCancelCoSubscriberCreation} />
			)}
			{step === EManualSubscriptionSteps.SELECT_PRODUCTS && (
				<SelectProducts clientSheetId={clientSheetId} subscriberType={subscriberType} cgpId={cgpId} officeId={officeId} isCoSubscription={isCoSubscription} />
			)}

			<AskCoSubscriberModal isOpen={askCoSubscriberModal.isOpen} onClose={askCoSubscriberModal.close} onNo={onCoSubscriberNo} onYes={onCoSubscriberYes} />
		</main>
	);
}
